import React, { useEffect } from "react";
import * as FormControlModule from "@mui/material/FormControl/index.js";
import * as FormHelperTextModule from "@mui/material/FormHelperText/index.js";
import * as InputLabelModule from "@mui/material/InputLabel/index.js";
import * as SelectModule from "@mui/material/Select/index.js";
import type { SelectChangeEvent } from "@mui/material/Select/SelectInput.js";
import * as StackModule from "@mui/material/Stack/index.js";
import * as TextFieldModule from "@mui/material/TextField/index.js";
import { resolveDefault } from "../../utils/moduleInterop";

const FormControl = resolveDefault<any>(FormControlModule);
const FormHelperText = resolveDefault<any>(FormHelperTextModule);
const InputLabel = resolveDefault<any>(InputLabelModule);
const Select = resolveDefault<any>(SelectModule);
const Stack = resolveDefault<any>(StackModule);
const TextField = resolveDefault<any>(TextFieldModule);

type CondSelectFieldProps = {
	label?: string;
	name: string;
	value: any;
	options: string[];
	error?: any;
	onChange: (e: SelectChangeEvent<{ name?: string; value: unknown }>) => void;
	helperText?: any;
	other?: {
		label: string;
		id: string;
	};
	id?: string;
	setFieldValue: (name: string, value: any) => void;
	disabled?: boolean;
	handleBlur?: () => void;
	placeholder?: string;
};

function CondSelectField(props: CondSelectFieldProps) {
	const [isOther, setIsOther] = React.useState(false);
	const {
		id,
		value,
		options,
		setFieldValue,
		name,
		onChange,
		disabled,
		label,
		handleBlur,
		helperText,
		placeholder,
		error,
	} = props;

	useEffect(() => {
		if (value) {
			if (!options.includes(value)) {
				setIsOther(true);
			} else if (value !== "Other (if other please specify)") {
				setIsOther(false);
			}
			setFieldValue(name, value);
		}
	}, [value]);

	const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFieldValue(name, e.currentTarget.value);
		setFieldValue(name + "other", e.currentTarget.value);
	};

	const handleSelectChange = (
		event: SelectChangeEvent<{ name?: string; value: unknown }>
	) => {
		const selectedValue = event.target.value as string; // Get the selected value directly from the event
		if (selectedValue === "Other (if other please specify)") {
			setIsOther(true);
		} else {
			setIsOther(false);
			setFieldValue(name, selectedValue);
			onChange(event); // Call the passed-in onChange handler
		}
	};

	return (
		<FormControl
			variant="outlined"
			sx={{ paddingRight: 10 }}
			fullWidth
			disabled={disabled}
			size="small"
		>
			<InputLabel htmlFor="outlined-age-native-simple">
				{label}
			</InputLabel>
			<Stack spacing={1}>
				<Select
					native
					name={name}
					value={isOther ? "Other (if other please specify)" : value}
					onBlur={handleBlur}
					onChange={handleSelectChange}
					label={label}
					autoWidth
					inputProps={{
						name: name,
						id: id,
					}}
				>
					<option aria-label="None" />
					{options.map((opt, index) => (
						<option key={index} value={opt}>
							{opt}
						</option>
					))}
				</Select>
				{helperText && <FormHelperText>{helperText}</FormHelperText>}
				{isOther && (
					<TextField
						id={id + "other"}
						label="Other"
						value={value}
						variant="outlined"
						placeholder={placeholder}
						name={name + "other"}
						onBlur={handleBlur}
						onChange={handleTextChange}
						fullWidth
						disabled={disabled}
						size="small"
						onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
							if (e.key === "Enter") e.preventDefault();
						}}
					/>
				)}
				{error && (
					<FormHelperText error sx={{ color: "red" }}>
						{error}
					</FormHelperText>
				)}
			</Stack>
		</FormControl>
	);
}

CondSelectField.defaultProps = {
	options: [],
};

export default CondSelectField;
